/**
 * @file    columnsBuilder.tsx
 * @scope   VIEW
 * @summary Builds the TanStack column definitions of DynamicTable and picks the cell variant
 *          per column/row (Strategy by world: enterprise → period delta → bar → link → plain).
 * @consumes cells, DataGridSorting, DynamicTableUtils, cellFormatting
 * @produces buildColumns
 * @sideEffects QUIRK: mutates `col.displayName` of an unnamed first column while rendering.
 */
import React from 'react';
import { ColumnHeader } from '@cx/ui';
import Tooltip from '../../DynamicTableTooltip';
import { enableSorting, getCustomSorting, SortingTypeValue } from 'helpers/DataGridSorting';
import {
  calculateColumnWidth,
  getComparisonValueFromRow,
  getSignificanceValueFromRow,
} from '../../common/utils/DynamicTableUtils';
import {
  formatCellValue,
  getCellValue,
  getPreviousField,
  isSingleQuestionGridColumn,
  openSurveyLink,
} from '../../common/utils/cellFormatting';
import { BarCell, EnterpriseCell, LinkCell, PlainCell } from './cells';

export interface ColumnsContext {
  payload: any;
  defaultResponse: any;
  decimalPlaces: any;
  displayTimePeriodDelta: boolean;
  selectedValueL1: any;
  showTotals: boolean;
  totalsFirstRow: React.MutableRefObject<any>;
  idSurveyPanel: any;
}

const renderCell = (col: any, row: any, index: number, ctx: ColumnsContext) => {
  const field = row?.[col?.field];
  // QUIRK: compares the cell value (not the field name) with 'field_0'.
  const skipDecimals = field === 'field_0' || isSingleQuestionGridColumn(col);
  const value = formatCellValue(getCellValue(field), skipDecimals, ctx.decimalPlaces);

  // ENTERPRISE-ONLY
  if (ctx.payload?.isEnterprise) {
    const indicator =
      typeof field === 'object' && field !== null
        ? getSignificanceValueFromRow(row, col?.field)
        : undefined;
    return <EnterpriseCell row={row} col={col} value={value} indicator={indicator} />;
  }

  if (ctx.displayTimePeriodDelta && index > 0) {
    const indicator = getComparisonValueFromRow(
      row,
      col.field,
      getPreviousField(col.field),
      ctx.decimalPlaces
    );
    return <EnterpriseCell row={row} col={col} value={value} indicator={indicator} />;
  }

  if (index === 0 && col.displayName === '') {
    col.displayName = ctx.selectedValueL1?.label
      ? ctx.selectedValueL1?.label
      : ctx.defaultResponse.data.controlData.defaultValueL1.label;
  }

  if (col.displayName === '') {
    return <BarCell value={row[`${col.field}`]} />;
  }

  if (col?.isLink) {
    return <LinkCell value={value} onClick={() => openSurveyLink(row, ctx.idSurveyPanel)} />;
  }

  return <PlainCell value={value} isPercentage={col.isPercentage} />;
};

const renderHeader = (col: any, isSortingEnabled: boolean) =>
  function Header({ column }: { column: any }) {
    if (col?.displayName === '') return null;
    return (
      <Tooltip text={col.displayName}>
        {isSortingEnabled ? (
          <ColumnHeader column={column} title={col.displayName} />
        ) : (
          col.displayName
        )}
      </Tooltip>
    );
  };

export const buildColumns = (data: any, type: SortingTypeValue, ctx: ColumnsContext) => {
  const sortingFn = getCustomSorting(type, ctx.showTotals, ctx.totalsFirstRow, 'asc');
  const columns = data?.columns || [];

  return columns
    .filter((col: any) => col.displayName !== 'Position')
    .map((col: any, index: number) => {
      const isSortingEnabled = enableSorting(col, ctx.defaultResponse, ctx.payload);
      return {
        id: col.field,
        accessorKey: col.field,
        header: renderHeader(col, isSortingEnabled),
        sortingFn,
        enableSorting: isSortingEnabled,
        cell: ({ cell }: { cell: any }) => renderCell(col, cell.row.original, index, ctx),
        meta: {
          columnStyle: {
            width:
              col.displayName === '' ? '20%' : calculateColumnWidth(col, data?.data ?? [], type),
            position: 'sticky',
            zIndex: 4,
            top: 0,
          },
          columnHeaderStyle: { cursor: 'default !important' },
        },
      };
    });
};
